import Page from 'PageNavigation/Page';
import Modal from 'PageNavigation/Modal';
import { injectable } from 'inversify';
import { IPageConfigurationProvider } from 'PageNavigation/IPageConfigurationProvider';
import { dicontainer } from 'DIContainer';
import { InterfaceNames } from 'InterfaceNames';
import Anchor from 'PageNavigation/Anchor';
import { Animation, DOMUtils, EasingFunctions, EventHandler, SimpleEventArgs } from 'ts-common-utils';

@injectable()
export default class PageNavigationController {
    public onAnchorChange = new EventHandler<SimpleEventArgs<Anchor | undefined>>();
    public onPageChange = new EventHandler<SimpleEventArgs<Page>>();
    public onShowModal = new EventHandler<SimpleEventArgs<Modal>>();
    public onCloseModal = new EventHandler();
    public onResize = new EventHandler();
    public onScroll = new EventHandler<SimpleEventArgs<Event>>();

    private _pageConfigProvider: IPageConfigurationProvider = dicontainer.get(InterfaceNames.IPageConfigurationProvider);
    private _scrollContainer?: HTMLElement;
    private _modalShown: boolean = false;
    private _activeAnchor?: Anchor;
    private _activePage?: Page;
    private _lockAnchor: boolean = false;

    constructor() {
        this.handleStateChange = this.handleStateChange.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.scrollTo = this.scrollTo.bind(this);
        this.updateActiveAnchor = this.updateActiveAnchor.bind(this);

        window.onpopstate = this.handleStateChange;
        window.onresize = this.handleWindowResize;

        this.switchFromUrlParams(this.getUrlParams(), false);
    }

    public get activeAnchor() {
        return this._activeAnchor;
    }

    public get activePage() {
        return this._activePage;
    }

    public switchFromUrlParams(urlParams: URLSearchParams, push: boolean = true) {
        this.switch(urlParams.get('pageName')!, urlParams.get('anchor')!, push);
    }

    public switch(pageName?: string, anchorName?: string, push = true) {
        let page = this._pageConfigProvider.pages.find(p => p.name == pageName);
        if (page == undefined) {
            if (this._activePage == undefined) {
                this.switch("accueil", undefined, true);
            }
            return;
        }
        let isDifferentPage = page != this._activePage;
        if (isDifferentPage) {
            if (page != undefined && page.component != undefined) {
                this._activePage = page;
            } else {
                return;
            }
            document.title = "La Barak | " + this._activePage.title;

            if (push && this._activePage != undefined) {
                this.push(this._activePage);
            } else if (this._activePage != undefined) {
                this.replaceState(this._activePage);
            }
            
            this.onPageChange.fire(this, new SimpleEventArgs(this._activePage));
        }
        if (anchorName != undefined) {
            let anchor = page.anchors.find(n => n.name == anchorName);
            if (anchor != undefined) {
                this.setActiveAnchor(anchor);
                if (isDifferentPage) {
                    window.setTimeout(this.scrollTo, 250, "#" + anchor.name);
                } else {
                    this.scrollTo("#" + anchor.name);
                }
            }
        } else if (this._scrollContainer?.scrollTop! > 0) {
            this._scrollContainer!.scrollTo(0, 0);
        } else {
            window.setTimeout(this.updateActiveAnchor, 100);
        }
    }
    
    public scrollTo(anchorSelector: string): boolean {
        let element = DOMUtils.findFirst(anchorSelector);
        if (element != undefined) {
            let start = this._scrollContainer!.scrollTop;
            let end = element.getBoundingClientRect().top + this._scrollContainer!.scrollTop - 80;
            this._lockAnchor = true;
            new Animation({
                duration: 400,
                progressFunction: (sender: object, effectiveProgress: number) => {
                    this._scrollContainer!.scrollTo(0, start + (end - start) * effectiveProgress);
                },
                easingFunction: EasingFunctions.easeOutExp,
                onCompleted: () => {
                    this._lockAnchor = false;
                }
            }).animate();
            return true;
        } else {
            console.warn("Cannot scroll to anchor. '" + anchorSelector + "' could not be found.");
            return false;
        }
    }

    public showModal(modal: Modal) {
        this.onShowModal.fire(this, new SimpleEventArgs(modal));
        this._modalShown = true;
        this.push(this._activePage!);
    }

    public closeModal() {
        history.go(-1);
    }

    public notifyAppMounted() {
        this._scrollContainer = document.querySelector(".app-container") as HTMLElement;
        this._scrollContainer.addEventListener("scroll", this.handleScroll)
    }

    private getUrlParams(): URLSearchParams {
        return new URLSearchParams(window.location.search);
    }

    private push(page: Page) {
        history.pushState(page.name, page.title, window.location.hostname != "" ? `${page.name}`: undefined);
    }

    private replaceState(page: Page) {
        history.replaceState(page.name, page.title, window.location.hostname != "" ? `${page.name}`: undefined);
    }

    private handleScroll(event: Event) {
        this.onScroll.fire(this, new SimpleEventArgs(event));
        this.updateActiveAnchor();
    }

    private handleWindowResize() {
        this.onResize.fire(this, new SimpleEventArgs(undefined));
    }

    private handleStateChange(event: PopStateEvent) {
        if (this._modalShown) {
            this._modalShown = false;
            this.onCloseModal.fire(this, new SimpleEventArgs(undefined));
        } else if (event.state != undefined) {
            this.switch(event.state, undefined, false);
        }
    }
    
    private isAnchorBelowTreshold(anchor: Anchor): boolean {
        let element = DOMUtils.findFirst("#" + anchor.name);
        if (element != undefined) {
            return element.getBoundingClientRect().top / this._scrollContainer!.clientHeight < 0.25;
        }
        return false;
    }

    private setActiveAnchor(anchor?: Anchor) {
        if (anchor != this._activeAnchor) {
            this._activeAnchor = anchor;
            this.onAnchorChange && this.onAnchorChange.fire(this, new SimpleEventArgs(anchor));
        }
    }

    private updateActiveAnchor() {
        if (this._lockAnchor) {
            return
        }
        let anchor = this._activePage!.anchors.lastOrDefault(a => this.isAnchorBelowTreshold(a));
        this.setActiveAnchor(anchor);
    }
}